import { Formik, useFormikContext } from "formik";
import { Check, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PageLoader } from "@/components/common/loader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { onboardingMe, onboardingSubmit } from "@/features/portal/api";
import { usePortalSessionStore } from "@/features/portal/store/portal-session-store";
import { portalHref } from "@/features/portal/portal-base";
import { ApiError } from "@/lib/api";
import { GuidelinesBox } from "./guidelines-box";
import { useOnboardingAutosave } from "./use-onboarding-autosave";
import { AddressTab } from "./tabs/address-tab";
import { BusinessTab } from "./tabs/business-tab";
import { ContactTab } from "./tabs/contact-tab";
import { FinishTab } from "./tabs/finish-tab";
import { IntroductionTab } from "./tabs/introduction-tab";
import { PersonalTab } from "./tabs/personal-tab";
import { SpouseTab } from "./tabs/spouse-tab";
import { type TabDef, TabStrip } from "./tab-strip";
import {
  clearOnboardingStep,
  clearOnboardingToken,
  getOnboardingStep,
  getOnboardingToken,
  setOnboardingStep,
} from "./token-store";
import {
  buildInitialValues,
  fieldTabIndex,
  makeValidationSchema,
  type OnboardingValues,
} from "./wizard-schema";

type ClientType = "INDIVIDUAL" | "BUSINESS";

const GUIDELINES = {
  intro: [
    "Answers save automatically as you move between fields.",
    "Use the tabs or the Previous / Next buttons to navigate.",
    "You can leave and come back using the same link while it's active.",
  ],
  personal: [
    "Enter your name exactly as it appears on your Social Security card.",
    "Your SSN is stored securely and is required to complete onboarding.",
    "Leave a phone field blank if it doesn't apply — partial numbers aren't accepted.",
  ],
  business: [
    "Use your legal business name as registered with the IRS.",
    "Your EIN is a 9-digit number in the format 12-3456789.",
    "Add a DBA only if your business operates under a different name.",
  ],
  spouse: [
    "Choose Married to enter your spouse's details.",
    "If married, spouse name, SSN, date of birth, and email are required.",
    "Community-property states may affect how liabilities are treated.",
  ],
  contact: ["Provide the best person for us to reach at the business.", "A valid email is important for portal notifications."],
  address: [
    "Choose Domestic for a U.S. address or International otherwise.",
    "Your county is looked up automatically once you pick a state.",
    "Add a mailing address only if it differs from your home address.",
  ],
  finish: [
    "Review each tab before submitting.",
    "Required fields are validated — we'll point out anything missing.",
  ],
} satisfies Record<string, string[]>;

interface TabConfig extends TabDef {
  key: string;
  guidelines: string[];
}

function tabsForType(clientType: ClientType): TabConfig[] {
  if (clientType === "INDIVIDUAL") {
    return [
      { label: "Introduction", key: "intro", guidelines: GUIDELINES.intro },
      { label: "Personal Information", key: "personal", guidelines: GUIDELINES.personal },
      { label: "Spouse Information", key: "spouse", guidelines: GUIDELINES.spouse },
      { label: "Address Information", key: "address", guidelines: GUIDELINES.address },
      { label: "Finish", key: "finish", guidelines: GUIDELINES.finish },
    ];
  }
  return [
    { label: "Introduction", key: "intro", guidelines: GUIDELINES.intro },
    { label: "Business Information", key: "business", guidelines: GUIDELINES.business },
    { label: "Contact Information", key: "contact", guidelines: GUIDELINES.contact },
    { label: "Address Information", key: "address", guidelines: GUIDELINES.address },
    { label: "Finish", key: "finish", guidelines: GUIDELINES.finish },
  ];
}

interface LoadedState {
  clientType: ClientType;
  initialValues: OnboardingValues;
}

/**
 * `/portal/onboarding` — the tabbed onboarding wizard. Reads the onboarding
 * token from sessionStorage; without it, bounces to the portal entry. Loads the
 * editable profile subset, brands the shell, and drives a single Formik form
 * across all tabs with per-field blur autosave.
 */
export function OnboardingWizard() {
  const navigate = useNavigate();
  const setBranding = usePortalSessionStore((s) => s.setBranding);
  const [token] = useState<string | null>(() => getOnboardingToken());
  const [loaded, setLoaded] = useState<LoadedState | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate(portalHref(), { replace: true });
      return;
    }
    let active = true;
    (async () => {
      try {
        const me = await onboardingMe(token);
        if (!active) return;
        if (me.branding) setBranding(me.branding);
        setLoaded({
          clientType: me.clientType,
          initialValues: buildInitialValues(me.profile),
        });
      } catch {
        if (!active) return;
        setFailed(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [token, navigate, setBranding]);

  // Token rejected mid-session — bounce back to the portal entry (in an effect,
  // never during render).
  useEffect(() => {
    if (failed) navigate(portalHref(), { replace: true });
  }, [failed, navigate]);

  if (!token || failed) return <PageLoader />;
  if (!loaded) return <PageLoader />;

  return (
    <Formik<OnboardingValues>
      initialValues={loaded.initialValues}
      validationSchema={makeValidationSchema(loaded.clientType)}
      validateOnMount={false}
      onSubmit={() => {
        /* submission handled explicitly in WizardBody's Finish action */
      }}
    >
      <WizardBody token={token} clientType={loaded.clientType} />
    </Formik>
  );
}

interface WizardBodyProps {
  token: string;
  clientType: ClientType;
}

function WizardBody({ token, clientType }: WizardBodyProps) {
  const navigate = useNavigate();
  const branding = usePortalSessionStore((s) => s.branding);
  const { validateForm, setTouched } = useFormikContext<OnboardingValues>();
  const tabs = useMemo(() => tabsForType(clientType), [clientType]);
  const lastStep = tabs.length - 1;

  const [step, setStep] = useState<number>(() => {
    const saved = getOnboardingStep();
    return saved <= lastStep ? saved : 0;
  });
  const [visited, setVisited] = useState<number[]>(() => [step]);
  const [errorTabs, setErrorTabs] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const { status, saveField } = useOnboardingAutosave(token);

  const goto = useCallback(
    (next: number) => {
      const target = Math.max(0, Math.min(next, lastStep));
      setStep(target);
      setVisited((v) => [...new Set([...v, target])]);
      setOnboardingStep(target);
    },
    [lastStep],
  );

  const fieldTabs = useMemo(() => fieldTabIndex(clientType), [clientType]);

  const handleFinish = useCallback(async () => {
    setSubmitting(true);
    // Client-side validation is UX only; the server re-validates on submit.
    const errors = await validateForm();
    const errorKeys = Object.keys(errors);
    if (errorKeys.length > 0) {
      const tabsWithErrors = [
        ...new Set(errorKeys.map((k) => fieldTabs[k]).filter((n) => n !== undefined)),
      ];
      setErrorTabs(tabsWithErrors);
      setTouched(
        errorKeys.reduce((acc, k) => ({ ...acc, [k]: true }), {}),
        false,
      );
      if (tabsWithErrors.length > 0) goto(Math.min(...tabsWithErrors));
      toast.error("Please complete the highlighted fields before finishing.");
      setSubmitting(false);
      return;
    }
    setErrorTabs([]);
    try {
      await onboardingSubmit(token);
      setShowConfirm(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        // Server field errors → mark + jump to the offending tab(s).
        const data = err.data as Record<string, unknown> | undefined;
        const serverErrors =
          data && typeof data.errors === "object"
            ? (data.errors as Record<string, unknown>)
            : {};
        const keys = Object.keys(serverErrors);
        const tabsWithErrors = [
          ...new Set(keys.map((k) => fieldTabs[k]).filter((n) => n !== undefined)),
        ];
        setErrorTabs(tabsWithErrors);
        if (tabsWithErrors.length > 0) goto(Math.min(...tabsWithErrors));
        toast.error(err.message || "Some fields need attention.");
      } else {
        toast.error(err instanceof ApiError ? err.message : "Couldn't submit. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }, [validateForm, fieldTabs, goto, setTouched, token]);

  const handleConfirmClose = useCallback(() => {
    setShowConfirm(false);
    clearOnboardingToken();
    clearOnboardingStep();
    navigate(portalHref("/onboarding/done"), { replace: true });
  }, [navigate]);

  // tabsForType always returns 5 tabs; `step` is clamped in goto().
  const currentTab = tabs[step] ?? tabs[0]!;

  function renderTab() {
    switch (currentTab.key) {
      case "intro":
        return <IntroductionTab branding={branding} />;
      case "personal":
        return <PersonalTab save={saveField} />;
      case "business":
        return <BusinessTab save={saveField} />;
      case "spouse":
        return <SpouseTab save={saveField} />;
      case "contact":
        return <ContactTab save={saveField} />;
      case "address":
        return <AddressTab save={saveField} token={token} />;
      case "finish":
        return <FinishTab onFinish={handleFinish} submitting={submitting} />;
      default:
        return null;
    }
  }

  return (
    <div className="mx-auto w-full max-w-6xl p-4 sm:p-6">
      <TabStrip
        tabs={tabs}
        current={step}
        visited={visited}
        errorTabs={errorTabs}
        onSelect={goto}
      />

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_20rem]">
        <Card>
          <CardContent className="pt-6">{renderTab()}</CardContent>
        </Card>

        <div className="grid content-start gap-4">
          <GuidelinesBox items={currentTab.guidelines} />
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <div className="text-muted-foreground flex items-center gap-2 text-xs">
          {status === "saving" && (
            <>
              <Loader2 className="size-3 animate-spin" /> Saving…
            </>
          )}
          {status === "saved" && (
            <>
              <Check className="size-3" /> Saved
            </>
          )}
          {status === "error" && <span className="text-destructive">Couldn&apos;t save a field</span>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => goto(step - 1)} disabled={step === 0}>
            <ChevronLeft className="size-4" /> Previous
          </Button>
          {step < lastStep && (
            <Button onClick={() => goto(step + 1)}>
              Next <ChevronRight className="size-4" />
            </Button>
          )}
        </div>
      </div>

      <Dialog open={showConfirm} onOpenChange={(open) => !open && handleConfirmClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Profile Information Complete</DialogTitle>
            <DialogDescription>
              Thank you — your profile has been submitted to your tax professional.
              You may now close this window.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={handleConfirmClose}>Continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
